/**
 * The React-side plumbing, and nothing else.
 *
 * Everything that decides anything lives in `sync/engine.ts`, `db/repository.ts`, or
 * `ui/status-text.ts`. What is left — subscribing to a store, running a query, not calling
 * `setState` on an unmounted component — is here, so that the parts worth testing are not
 * trapped behind a renderer.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import type { SyncEngine, SyncSnapshot } from '../sync/engine.js';

const STARTING: SyncSnapshot = {
  state: 'unconfigured',
  counts: { pending: 0, synced: 0, failed: 0, photosHeldLocally: 0, bytesHeldLocally: 0 },
};

/**
 * Subscribe to the sync engine.
 *
 * `useSyncExternalStore` rather than an effect-plus-state pair because the engine is a real
 * external store: it changes from timers and network callbacks that React knows nothing
 * about, and tearing — half the screen showing "uploaded" while the other half shows
 * "queued" — is exactly the confusion this UI cannot afford.
 */
export function useSyncSnapshot(engine: SyncEngine | undefined): SyncSnapshot {
  const subscribe = useCallback(
    (onChange: () => void) => engine?.subscribe(onChange) ?? (() => undefined),
    [engine],
  );
  const read = useCallback(() => engine?.snapshot() ?? STARTING, [engine]);

  return useSyncExternalStore(subscribe, read, read);
}

export interface AsyncValue<T> {
  readonly value: T | undefined;
  readonly loading: boolean;
  readonly error: string | undefined;
  /** Re-run the query. Called after every write, since nothing here is reactive. */
  readonly reload: () => void;
}

/**
 * Run an async query and keep the latest answer.
 *
 * Two safeguards, both for the same failure: showing a rep stale or wrong state about their
 * own evidence. A generation counter discards results from a superseded run, so a slow
 * query for job A cannot land after the user has already opened job B; and the unmount flag
 * stops a late resolution from writing into a dead component.
 */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): AsyncValue<T> {
  const [state, setState] = useState<{ value: T | undefined; loading: boolean; error: string | undefined }>({
    value: undefined,
    loading: true,
    error: undefined,
  });
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const mine = generation.current + 1;
    generation.current = mine;
    let alive = true;

    setState((previous) => ({ ...previous, loading: true }));

    load()
      .then((value) => {
        if (!alive || generation.current !== mine) return;
        setState({ value, loading: false, error: undefined });
      })
      .catch((error: unknown) => {
        if (!alive || generation.current !== mine) return;
        setState({
          value: undefined,
          loading: false,
          error: error instanceof Error ? error.message : String(error),
        });
      });

    return () => {
      alive = false;
    };
    // `load` is intentionally not a dependency: callers pass an inline closure, and
    // depending on it would re-run the query on every render. The caller declares what the
    // query actually depends on.
  }, [...deps, nonce]);

  return { ...state, reload };
}

/**
 * Connectivity, as the browser reports it.
 *
 * `navigator.onLine` only knows whether there is a network interface, not whether our API
 * is reachable — which is why the sync banner's authority is the outbox draining, not this
 * flag. This is good enough to decide *when to try*, and that is all it is used for.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(readOnline);

  useEffect(() => {
    const update = () => {
      setOnline(readOnline());
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    update();
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

/**
 * Read connectivity, defaulting to connected when the platform cannot tell us.
 *
 * Node 21+ defines a global `navigator` with no `onLine` property, so an existence check
 * yields `undefined` — falsy — and renders a permanent "Offline" banner during server
 * rendering. Telling a crew they are offline when we simply do not know is the worse error.
 */
export function readOnline(): boolean {
  const status: unknown = globalThis.navigator?.onLine;
  return typeof status === 'boolean' ? status : true;
}
